import {getLocaleText} from '../util/translation';

const COUPON_ERROR_SELECTORS = '.woocommerce-error, .is-error';
const INLINE_COUPON_ERROR_SELECTOR = '.wc-invalid-coupon';
const COUPON_INPUT_SELECTOR = '.wc-coupon-code-input';

export function couponResponseHasError(responseHtml: string): boolean {
	return responseHtml.includes('woocommerce-error') || responseHtml.includes('is-error');
}

export function extractCouponResponseText(responseHtml: string): string {
	const $container = document.createElement('div');
	$container.innerHTML = responseHtml;

	const $notice = $container.querySelector<HTMLElement>(COUPON_ERROR_SELECTORS) ?? $container;

	return ($notice.textContent ?? '').replace(/\s+/g, ' ').trim();
}

export function showCouponError($form: HTMLFormElement, responseHtml: string): void {
	const $error = findCouponErrorElement($form);
	const $input = findCouponInput($form);

	if (!$error || !$input) {
		return;
	}

	ensureCouponErrorId($error);
	$form.classList.remove('hide');
	renderCouponErrorContent($error, $form, extractCouponResponseText(responseHtml));
	$error.setAttribute('role', 'alert');
	$error.classList.remove('hide');
	$input.setAttribute('aria-invalid', 'true');
	addAriaDescription($input, $error.id);
	$input.focus();
}

export function clearCouponError($form: HTMLFormElement): void {
	const $error = findCouponErrorElement($form);
	const $input = findCouponInput($form);

	if ($error) {
		$error.replaceChildren();
		$error.classList.add('hide');
	}

	if ($input) {
		$input.setAttribute('aria-invalid', 'false');

		if ($error?.id) {
			removeAriaDescription($input, $error.id);
		}
	}
}

function renderCouponErrorContent($error: HTMLElement, $form: HTMLFormElement, message: string): void {
	const $message = document.createElement('span');
	$message.textContent = message;

	const $dismiss = document.createElement('button');
	$dismiss.type = 'button';
	$dismiss.className = 'pp-coupon-error-dismiss';
	$dismiss.setAttribute('aria-label', getLocaleText('Dismiss coupon error'));
	$dismiss.addEventListener('click', () => {
		clearCouponError($form);
	});

	$error.replaceChildren($message, $dismiss);
}

function findCouponErrorElement($form: HTMLFormElement): HTMLElement | null {
	return $form.parentElement?.querySelector<HTMLElement>(INLINE_COUPON_ERROR_SELECTOR) ?? $form.querySelector<HTMLElement>(INLINE_COUPON_ERROR_SELECTOR);
}

function findCouponInput($form: HTMLFormElement): HTMLInputElement | null {
	return $form.querySelector<HTMLInputElement>(COUPON_INPUT_SELECTOR);
}

function ensureCouponErrorId($error: HTMLElement): void {
	if ($error.id) {
		return;
	}

	const $errors = Array.from(document.querySelectorAll<HTMLElement>(INLINE_COUPON_ERROR_SELECTOR));
	const errorIndex = $errors.indexOf($error);
	$error.id = `pp-coupon-error-${errorIndex >= 0 ? errorIndex : $errors.length}`;
}

function addAriaDescription($input: HTMLInputElement, id: string): void {
	const existingIds = ($input.getAttribute('aria-describedby') ?? '').split(' ').filter(Boolean);

	if (!existingIds.includes(id)) {
		existingIds.push(id);
	}

	$input.setAttribute('aria-describedby', existingIds.join(' '));
}

function removeAriaDescription($input: HTMLInputElement, id: string): void {
	const remainingIds = ($input.getAttribute('aria-describedby') ?? '').split(' ').filter(existingId => existingId && existingId !== id);

	if (remainingIds.length > 0) {
		$input.setAttribute('aria-describedby', remainingIds.join(' '));
		return;
	}

	$input.removeAttribute('aria-describedby');
}
